import { apiClient, ApiClientError } from '@/shared/lib/api';
import {
  buildQueryString as buildQueryStringUtil,
  type QueryParams,
} from '@/shared/lib/query-params';

// JSON:API compliant response structure
export interface JsonApiResponse<T, I = Record<string, unknown>> {
  data: T;
  included?: I[];
  meta?: {
    pagination?: {
      total: number;
      count: number;
      per_page: number;
      current_page: number;
      total_pages: number;
    };
    [key: string]: unknown;
  };
  links?: {
    self?: string;
    first?: string;
    last?: string;
    next?: string;
    prev?: string;
    [key: string]: string | undefined;
  };
}

// JSON:API resource shape — every server-side resource serializes to this.
export interface JsonApiResource<A extends object = Record<string, unknown>> {
  type: string;
  id: string;
  attributes: A;
  relationships?: Record<string, unknown>;
}

// Service options for customizing behavior
export interface ServiceOptions {
  loading?: {
    trackEndpoint?: string;
    silent?: boolean;
  };
  error?: {
    silent?: boolean;
    /**
     * Statuses this endpoint answers as normal control flow — logged at
     * `debug` instead of `error`, while everything else stays loud. See
     * `FetchOptions.expectedStatuses`.
     */
    expectedStatuses?: number[];
    customHandler?: (error: ApiClientError) => void;
  };
  cache?: {
    key?: string;
    ttl?: number;
  };
  /**
   * Cancellation signal — pass TanStack Query's `queryFn({ signal })` here so a
   * request dies with the screen that asked for it instead of racing the next
   * one (fast prev/next paging through concepts is the common case). Composed
   * with the client's own request deadline in `ApiClient.performFetch`.
   */
  signal?: AbortSignal;
}

/**
 * Base API Service with consistent error handling and JSON:API formatting.
 */
export class BaseApiService {
  protected async request<T>(
    endpoint: string,
    options: RequestInit & { skipAuth?: boolean; silentErrors?: boolean } = {},
    serviceOptions: ServiceOptions = {}
  ): Promise<T> {
    try {
      return await apiClient.fetch<T>(endpoint, {
        ...options,
        // An explicit `signal` on the RequestInit wins; otherwise take the one
        // the caller passed through ServiceOptions.
        signal: options.signal ?? serviceOptions.signal,
        skipAuth: options.skipAuth,
        // serviceOptions.error.silent doubles as a hint to demote API/network
        // error logging from `error` to `debug`. Used for endpoints where 4xx
        // is part of the expected response surface (e.g. loading-facts).
        silentErrors: options.silentErrors ?? serviceOptions.error?.silent,
        expectedStatuses: serviceOptions.error?.expectedStatuses,
      });
    } catch (error) {
      const apiError =
        error instanceof ApiClientError
          ? error
          : new ApiClientError(
              {
                message:
                  error instanceof Error ? error.message : 'Unknown error',
              },
              0
            );

      if (!serviceOptions.error?.silent && serviceOptions.error?.customHandler) {
        serviceOptions.error.customHandler(apiError);
      }

      throw apiError;
    }
  }

  /**
   * GET request with enhanced features.
   * Named `httpGet` (not `get`) so that subclasses can expose a public
   * `get(id)` method following CRUD conventions without colliding with the
   * HTTP-verb method name.
   */
  protected async httpGet<T>(
    endpoint: string,
    options?: RequestInit & { skipAuth?: boolean; silentErrors?: boolean },
    serviceOptions?: ServiceOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'GET',
        ...options,
      },
      serviceOptions
    );
  }

  /**
   * POST request with enhanced features and JSON:API formatting
   */
  protected async post<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestInit & { skipAuth?: boolean; silentErrors?: boolean },
    serviceOptions?: ServiceOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
        ...options,
      },
      serviceOptions
    );
  }

  /**
   * PUT request with enhanced features and JSON:API formatting
   */
  protected async put<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestInit & { skipAuth?: boolean; silentErrors?: boolean },
    serviceOptions?: ServiceOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
        ...options,
      },
      serviceOptions
    );
  }

  /**
   * PATCH request with enhanced features and JSON:API formatting
   */
  protected async patch<T, D = unknown>(
    endpoint: string,
    data?: D,
    options?: RequestInit & { skipAuth?: boolean; silentErrors?: boolean },
    serviceOptions?: ServiceOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
        ...options,
      },
      serviceOptions
    );
  }

  /**
   * DELETE request with enhanced features.
   * Named `httpDelete` so subclasses can expose a public `delete(id)` /
   * `remove(id)` method without colliding with the HTTP-verb method name.
   */
  protected async httpDelete<T>(
    endpoint: string,
    options?: RequestInit & { skipAuth?: boolean; silentErrors?: boolean },
    serviceOptions?: ServiceOptions
  ): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'DELETE',
        ...options,
      },
      serviceOptions
    );
  }

  /**
   * Batch request helper for multiple parallel requests
   */
  protected async batch<T extends readonly unknown[]>(requests: {
    [K in keyof T]: () => Promise<T[K]>;
  }): Promise<T> {
    return Promise.all(requests.map((req) => req())) as unknown as Promise<T>;
  }

  /**
   * Request with automatic pagination handling
   */
  protected async *paginate<T>(
    endpoint: string,
    pageSize: number = 20,
    serviceOptions?: ServiceOptions
  ): AsyncGenerator<T[], void, unknown> {
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const params = new URLSearchParams({
        'page[number]': page.toString(),
        'page[size]': pageSize.toString(),
      });

      const url = `${endpoint}?${params.toString()}`;
      const response = await this.httpGet<JsonApiResponse<T[]>>(
        url,
        undefined,
        serviceOptions
      );

      yield response.data;

      // Check if there are more pages
      const totalPages = response.meta?.pagination?.total_pages || 1;
      hasMore = page < totalPages;
      page++;
    }
  }

  /**
   * Helper to build query string from parameters
   * @see buildQueryString from '@/shared/lib/query-params' for standalone usage
   */
  protected buildQueryString(params: QueryParams): string {
    return buildQueryStringUtil(params);
  }

  /**
   * Flatten a JSON:API resource: merge `id` into the attribute object so
   * callers consume `{ id, ...attributes }` directly. Use when the server's
   * domain object lives entirely under `attributes` and the consumer doesn't
   * walk relationships.
   */
  protected normalizeOne<A extends object>(
    response: JsonApiResponse<JsonApiResource<A>>
  ): A & { id: string } {
    return { ...response.data.attributes, id: response.data.id };
  }

  /**
   * Same as normalizeOne for a list response.
   */
  protected normalizeMany<A extends object>(
    response: JsonApiResponse<JsonApiResource<A>[]>
  ): Array<A & { id: string }> {
    return response.data.map((item) => ({ ...item.attributes, id: item.id }));
  }

  /**
   * Format data for JSON:API compliance
   */
  protected formatJsonApiData<T extends Record<string, unknown>>(
    type: string,
    attributes: T,
    relationships?: Record<string, unknown>,
    id?: string
  ): {
    data: {
      type: string;
      id?: string;
      attributes?: T;
      relationships?: Record<string, unknown>;
    };
  } {
    const hasAttributes = attributes && Object.keys(attributes).length > 0;

    return {
      data: {
        type,
        ...(id && { id }),
        ...(hasAttributes && { attributes }),
        ...(relationships && { relationships }),
      },
    };
  }
}
